import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { CircularProgress } from "@material-ui/core";
import _ from "lodash";
import ConnectionCard from "../../components/ProfileViewNew/components/ConnectionCard";
import { FlashMessage, Spinner } from "../../components/common";
import { rootReducersState } from "../../reducers";
import { connectionRequestStatus } from "../../utils/appConstants";
import profileRecommendationsServices from "../../services/profileRecommendations.services";

const PAGE_SIZE = 20;

const mapConnectionStatus = (list: any[]) => {
  const statusMap: { [key: number]: any } = {};
  list.forEach((recommendation: any) => {
    if (recommendation?.follow) {
      const statusText =
        connectionRequestStatus.find(
          (status) => status.status_id === recommendation.follow.status
        )?.status_text || "Connect";

      statusMap[recommendation.id] = {
        connect_id: recommendation.follow.connect_id,
        status_id: recommendation.follow.status,
        status_text: statusText,
      };
    }
  });
  return statusMap;
};

const SuggestedConnections = () => {
  const currentUserId = useSelector(({ session }: rootReducersState) =>
    _.get(session, "currentUser.id", null)
  );

  const [profiles, setProfiles] = useState<any[]>([]);
  const [connectionRequests, setConnectionRequests] = useState<{
    [key: number]: any;
  }>({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchingRef = useRef(false);
  const pageRef = useRef(1);
  const totalPagesRef = useRef(1);

  const loadPage = useCallback(
    async (nextPage: number, append: boolean) => {
      if (!currentUserId || fetchingRef.current) return;
      fetchingRef.current = true;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const response =
          await profileRecommendationsServices.getProfileRecommendations(
            currentUserId,
            { page: nextPage, pageSize: PAGE_SIZE }
          );

        if (!response.flag) {
          if (!append) setProfiles([]);
          FlashMessage(
            response.message || "Failed to load recommended profiles",
            "error"
          );
          return;
        }

        const list = _.get(response, "data.candidate_recommendations", []) || [];
        const pagination = _.get(response, "pagination", {}) || {};
        const nextTotalPages = Number(pagination.totalPages) || 1;

        pageRef.current = nextPage;
        totalPagesRef.current = nextTotalPages;
        setConnectionRequests((prev) => ({
          ...prev,
          ...mapConnectionStatus(list),
        }));
        setProfiles((prev) => {
          if (!append) return list;
          const existingIds = new Set(prev.map((item) => item.id));
          return [
            ...prev,
            ...list.filter((item: any) => !existingIds.has(item.id)),
          ];
        });
      } catch (error) {
        FlashMessage("Error loading recommended profiles", "error");
      } finally {
        fetchingRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [currentUserId]
  );

  useEffect(() => {
    pageRef.current = 1;
    totalPagesRef.current = 1;
    setProfiles([]);
    if (currentUserId) {
      loadPage(1, false);
    } else {
      setLoading(false);
    }
  }, [currentUserId, loadPage]);

  useEffect(() => {
    const onScroll = _.debounce(() => {
      const docBottom =
        document.documentElement.getBoundingClientRect().bottom - 800;
      const reachedBottom = window.innerHeight > docBottom;
      if (
        reachedBottom &&
        !fetchingRef.current &&
        pageRef.current < totalPagesRef.current
      ) {
        loadPage(pageRef.current + 1, true);
      }
    }, 300);

    window.addEventListener("scroll", onScroll);
    return () => {
      onScroll.cancel();
      window.removeEventListener("scroll", onScroll);
    };
  }, [loadPage]);

  const handleConnectionUpdate = (id: number, status: any) => {
    setConnectionRequests((prev) => ({
      ...prev,
      [id]: status,
    }));
  };

  const handleProfileRemove = async (profileId: number) => {
    try {
      const response =
        await profileRecommendationsServices.removeProfileRecommendation(
          profileId
        );

      if (response.flag) {
        setProfiles((prev) => prev.filter((rec: any) => rec.id !== profileId));
        FlashMessage("Profile removed successfully", "success");
      } else {
        FlashMessage(response.message || "Failed to remove profile", "error");
      }
    } catch (error) {
      FlashMessage("Error removing profile", "error");
    }
  };

  const showEmpty = !loading && profiles.length === 0;

  return (
    <Spinner visible={loading && profiles.length === 0}>
      <div className="profile-container suggested-connections-page">
        <div className="suggested-connection-wrapper">
          <div className="suggested-connection-head">
            <h4>Suggested Connections</h4>
          </div>
          {loading && profiles.length === 0 ? (
            <div className="suggested-connections-loading">
              <CircularProgress color="secondary" size={24} />
            </div>
          ) : showEmpty ? (
            <p className="suggested-connections-empty">
              No recommended profiles right now. Check back later.
            </p>
          ) : (
            <>
              <div className="suggested-cards-wrapper">
                {profiles.map((recommendation: any, index: number) => (
                  <ConnectionCard
                    key={`${recommendation.id}-${index}`}
                    index={index}
                    recommendation={recommendation}
                    connectionStatus={connectionRequests[recommendation.id]}
                    onConnectionUpdate={handleConnectionUpdate}
                    onProfileRemove={handleProfileRemove}
                  />
                ))}
              </div>
              {loadingMore && (
                <div className="suggested-connections-loading">
                  <CircularProgress color="secondary" size={24} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Spinner>
  );
};

export default SuggestedConnections;
